import { z } from 'zod';
import { DEFAULT_LOCATION_ID, effectiveLocationId } from './types';
import type { Period } from './period';

/**
 * Ventas por plato CON FECHAS (fase 7, oct 2026).
 *
 * Antes cada carga de unidades vendidas (a mano, Excel o punto de venta)
 * pisaba la anterior en `MenuItem.unitsSold` y nadie sabía de qué fechas era.
 * Ahora cada carga se guarda como un periodo (desde–hasta, local, origen) y
 * «Qué plato te conviene más» dice con qué ventas calcula: «Con tus ventas del
 * 1 al 30 de septiembre». Con las mismas cargas se calcula el gasto teórico
 * del recuento (lo que deberías haber gastado según tus recetas).
 *
 * `MenuItem.unitsSold` se sigue escribiendo con la carga MÁS RECIENTE: es lo
 * que leen la app nativa, el simulador y las cargas viejas sin fechas, que se
 * muestran como «tu última carga».
 */

export type DishSalesSource = 'manual' | 'excel' | 'tpv';

export interface DishSalesLine {
  menuItemId: string;
  units: number;
}

export interface DishSalesPeriod {
  tenantId: string;
  /** `<start>_<end>_<local>`: una carga por rango y local; volver a subirla la sustituye. */
  salesId: string;
  /** YYYY-MM-DD, ambos incluidos. */
  start: string;
  end: string;
  /** Local; ausente = principal (como el resto de registros). */
  locationId?: string;
  source: DishSalesSource;
  lines: DishSalesLine[];
  totalUnits: number;
  updatedAt: string;
}

export const dishSalesInputSchema = z
  .object({
    start: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Fecha inicial no válida'),
    end: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Fecha final no válida'),
    locationId: z.string().trim().min(1).max(80).optional(),
    source: z.enum(['manual', 'excel', 'tpv']).default('manual'),
    lines: z
      .array(z.object({ menuItemId: z.string().min(1).max(120), units: z.number().int().min(0).max(10_000_000) }))
      .max(2_000, 'Demasiados platos en una carga.'),
  })
  .refine((v) => v.start <= v.end, { message: 'La fecha final tiene que ser igual o posterior a la inicial.' })
  .refine((v) => daysBetween(v.start, v.end) <= 366, { message: 'Una carga de ventas no puede pasar de un año.' });

export type DishSalesInput = z.infer<typeof dishSalesInputSchema>;

/* ------------------------------------------------------------------ */
/* Fechas                                                              */
/* ------------------------------------------------------------------ */

const toUtc = (iso: string): number => Date.UTC(Number(iso.slice(0, 4)), Number(iso.slice(5, 7)) - 1, Number(iso.slice(8, 10)));

/** Suma días a una fecha YYYY-MM-DD. */
export function addDays(iso: string, days: number): string {
  return new Date(toUtc(iso) + days * 86_400_000).toISOString().slice(0, 10);
}

/** Días entre dos fechas, AMBAS incluidas (del 1 al 30 = 30). */
export function daysBetween(start: string, end: string): number {
  return Math.round((toUtc(end) - toUtc(start)) / 86_400_000) + 1;
}

/** Último día de un mes YYYY-MM. */
export function lastDayOfMonth(month: string): string {
  const y = Number(month.slice(0, 4));
  const m = Number(month.slice(5, 7));
  const d = new Date(Date.UTC(y, m, 0)).getUTCDate();
  return `${month}-${String(d).padStart(2, '0')}`;
}

const MESES = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];

/** «septiembre» */
export function nombreMes(month: string): string {
  return MESES[Number(month.slice(5, 7)) - 1] ?? month;
}

/** «30 de septiembre» (con año si se pide). */
export function fechaLarga(iso: string, conAnyo = false): string {
  const dia = Number(iso.slice(8, 10));
  return `${dia} de ${nombreMes(iso.slice(0, 7))}${conAnyo ? ` de ${iso.slice(0, 4)}` : ''}`;
}

/**
 * Un rango en palabras, como lo diría una persona:
 * «del 1 al 30 de septiembre», «del 15 de agosto al 15 de septiembre»,
 * «el 3 de octubre», «del 20 de diciembre de 2025 al 10 de enero de 2026».
 */
export function rangoEnPalabras(start: string, end: string): string {
  if (start === end) return `el ${fechaLarga(start)}`;
  if (start.slice(0, 4) !== end.slice(0, 4)) return `del ${fechaLarga(start, true)} al ${fechaLarga(end, true)}`;
  if (start.slice(0, 7) === end.slice(0, 7)) return `del ${Number(start.slice(8, 10))} al ${fechaLarga(end)}`;
  return `del ${fechaLarga(start)} al ${fechaLarga(end)}`;
}

/* ------------------------------------------------------------------ */
/* Cargas                                                              */
/* ------------------------------------------------------------------ */

export function dishSalesId(start: string, end: string, locationId?: string): string {
  return `${start}_${end}_${effectiveLocationId(locationId)}`;
}

/** Normaliza una carga: sin líneas a 0 repetidas, total calculado, principal sin locationId. */
export function buildDishSalesPeriod(
  tenantId: string,
  input: DishSalesInput,
  now = new Date().toISOString(),
): DishSalesPeriod {
  const byItem = new Map<string, number>();
  for (const line of input.lines) {
    if (line.units > 0) byItem.set(line.menuItemId, (byItem.get(line.menuItemId) ?? 0) + line.units);
  }
  const lines = [...byItem.entries()].map(([menuItemId, units]) => ({ menuItemId, units }));
  const location = effectiveLocationId(input.locationId);
  return {
    tenantId,
    salesId: dishSalesId(input.start, input.end, location),
    start: input.start,
    end: input.end,
    ...(location !== DEFAULT_LOCATION_ID ? { locationId: location } : {}),
    source: input.source ?? 'manual',
    lines,
    totalUnits: lines.reduce((a, l) => a + l.units, 0),
    updatedAt: now,
  };
}

/**
 * ¿Esta carga es la más reciente de su local? Solo entonces se copia a
 * `MenuItem.unitsSold` («tu última carga»): subir hoy las ventas de agosto
 * no debe pisar las de septiembre.
 */
export function isLatestDishSales(period: Pick<DishSalesPeriod, 'end' | 'locationId' | 'salesId'>, all: DishSalesPeriod[]): boolean {
  const loc = effectiveLocationId(period.locationId);
  return !all.some(
    (p) => p.salesId !== period.salesId && effectiveLocationId(p.locationId) === loc && p.end > period.end,
  );
}

/** Lo más largo que se puede pedir de ventas por plato de una vez. */
export const MAX_SALES_RANGE_DAYS = 366;

/**
 * `?salesStart&salesEnd` de Precios. Sin ninguno de los dos = sin rango (la
 * última carga). Fechas que no lo son, al revés o de más de un año: error,
 * para que nadie pida siglos de días a `unitsInRange`.
 */
export function salesRangeOf(start?: string, end?: string): { range?: Period; error?: string } {
  if (!start && !end) return {};
  const iso = /^\d{4}-\d{2}-\d{2}$/;
  const valida = (d?: string): d is string => Boolean(d && iso.test(d) && !Number.isNaN(Date.parse(`${d}T00:00:00Z`)) && new Date(`${d}T00:00:00Z`).toISOString().slice(0, 10) === d);
  if (!valida(start) || !valida(end)) return { error: 'Las fechas de las ventas no son válidas.' };
  if (start > end) return { error: 'La fecha final tiene que ser igual o posterior a la inicial.' };
  if (daysBetween(start, end) > MAX_SALES_RANGE_DAYS) return { error: 'Elige un rango de ventas de un año como mucho.' };
  return { range: { start, end } };
}

/**
 * Unidades por plato en un rango, sin contar dos veces el mismo día.
 *
 * Cada carga se reparte por igual entre sus días (300 platos en 30 días = 10
 * al día). Si dos cargas del mismo local cubren el mismo día, manda la que se
 * subió después: el usuario corrigió. Un rango que solo cubre parte de una
 * carga se lleva su parte proporcional.
 *
 * Sin `locationId` suma todos los locales.
 */
export function unitsInRange(
  periods: DishSalesPeriod[],
  range: Period,
  locationId?: string,
): { units: Map<string, number>; coveredDays: number; days: number } {
  const units = new Map<string, number>();
  const days = Math.max(0, daysBetween(range.start, range.end));
  const scoped = locationId
    ? periods.filter((p) => effectiveLocationId(p.locationId) === effectiveLocationId(locationId))
    : periods;
  const byLocation = new Map<string, DishSalesPeriod[]>();
  for (const p of scoped) {
    if (p.end < range.start || p.start > range.end) continue;
    const loc = effectiveLocationId(p.locationId);
    byLocation.set(loc, [...(byLocation.get(loc) ?? []), p]);
  }
  const weight = new Map<DishSalesPeriod, number>();
  const covered = new Set<string>();
  for (const list of byLocation.values()) {
    for (let d = range.start; d <= range.end; d = addDays(d, 1)) {
      let best: DishSalesPeriod | undefined;
      for (const p of list) {
        if (p.start > d || p.end < d) continue;
        if (!best || p.updatedAt > best.updatedAt) best = p;
      }
      if (!best) continue;
      covered.add(d);
      weight.set(best, (weight.get(best) ?? 0) + 1 / daysBetween(best.start, best.end));
    }
  }
  for (const [p, w] of weight) {
    for (const line of p.lines) units.set(line.menuItemId, (units.get(line.menuItemId) ?? 0) + line.units * w);
  }
  return { units, coveredDays: covered.size, days };
}

export interface DishSalesOption {
  start: string;
  end: string;
  /** Origen de la carga; un mes del punto de venta junta muchos días. */
  source: DishSalesSource;
  totalUnits: number;
}

/**
 * Periodos que se pueden elegir en «Qué plato te conviene más», del más
 * reciente al más antiguo. Cada carga a mano o de Excel es una opción con sus
 * fechas; lo que trae el punto de venta (días sueltos) se junta por meses.
 */
export function dishSalesOptions(periods: DishSalesPeriod[], locationId?: string): DishSalesOption[] {
  const scoped = locationId
    ? periods.filter((p) => effectiveLocationId(p.locationId) === effectiveLocationId(locationId))
    : periods;
  const options = new Map<string, DishSalesOption>();
  for (const p of scoped) {
    if (p.source !== 'tpv') {
      const key = `${p.start}_${p.end}`;
      const prev = options.get(key);
      options.set(key, {
        start: p.start,
        end: p.end,
        source: prev && prev.source !== p.source ? 'manual' : p.source,
        totalUnits: (prev?.totalUnits ?? 0) + p.totalUnits,
      });
      continue;
    }
    // Punto de venta: por mes, recortado a los días que hay.
    for (let month = p.start.slice(0, 7); month <= p.end.slice(0, 7); month = nextMonth(month)) {
      const start = p.start > `${month}-01` ? p.start : `${month}-01`;
      const end = p.end < lastDayOfMonth(month) ? p.end : lastDayOfMonth(month);
      const key = `tpv_${month}`;
      const prev = options.get(key);
      const share = Math.round((p.totalUnits * daysBetween(start, end)) / daysBetween(p.start, p.end));
      options.set(key, {
        start: prev && prev.start < start ? prev.start : start,
        end: prev && prev.end > end ? prev.end : end,
        source: 'tpv',
        totalUnits: (prev?.totalUnits ?? 0) + share,
      });
    }
  }
  return [...options.values()].sort((a, b) => b.end.localeCompare(a.end) || b.start.localeCompare(a.start));
}

function nextMonth(month: string): string {
  const y = Number(month.slice(0, 4));
  const m = Number(month.slice(5, 7));
  return m === 12 ? `${y + 1}-01` : `${y}-${String(m + 1).padStart(2, '0')}`;
}

/**
 * Rango que propone «Subir ventas de octubre»: el mes siguiente a la última
 * carga (o el mes en curso si no hay ninguna), sin pasar de hoy.
 */
export function nextDishSalesRange(latest: { end: string } | undefined, today: string): Period & { month: string } {
  const month = latest ? nextMonth(latest.end.slice(0, 7)) : today.slice(0, 7);
  const capped = month > today.slice(0, 7) ? today.slice(0, 7) : month;
  const end = lastDayOfMonth(capped) < today ? lastDayOfMonth(capped) : today;
  return { start: `${capped}-01`, end, month: capped };
}
